/**
 * AtlasContractBoardModule — the ground contract board (TOWN LIFE, 2026-09-24).
 *
 * E at a town's contract board opens it:
 *   BOARD  what this town's board carries: players' COURIER parcels and ESCORT jobs posted here (take them — a
 *          courier posts collateral), and the town's own RELIEF orders (food it is short of — deliver from your
 *          own stacks, paid pro rata) and caravan ESCORTS (walk beside a caravan that just left).
 *   POST   a courier parcel (up to 8 real items and/or a stack, a destination town, a reward and collateral, an
 *          optional recipient) or an escort for yourself (someone walks with you to a town; sampled on the road).
 *          The reward and a 5 % board fee are escrowed; an untaken contract lapses back to you.
 *   MINE   what you carry and where to (waypoint to the destination board), what you posted (cancel, collect,
 *          sign off an escort at the destination board), and a parcel waiting for YOU.
 * Levers: `__contractBoard`.
 */
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { FAMILY_BY_KEY } from '../lib/resource_catalog.js';
import {
  CONTRACT_MIN_REWARD, CONTRACT_HOURS_DEFAULT, PARCEL_MAX_ITEMS, contractFee, untilText, compassOf,
} from '../lib/town_life.js';
import { townName, townList } from '../lib/town_names.js';
import { el, btn, input, select, tabs, head, chip, muted, field, card, act, debounced, ms } from './town/town_ui.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';

interface ContractRow {
  contractId: bigint; kind: string; issuerKind: string; issuer: Identity; originSid: bigint; destSid: bigint; title: string; note: string;
  reward: bigint; collateral: bigint; commodity: string; qty: number; delivered: number; caravanId: bigint; recipient: Identity; state: string;
  taker: Identity; deadline: { microsSinceUnixEpoch: bigint }; postedAt: { microsSinceUnixEpoch: bigint }; hours: number;
}
interface ParcelRow { parcelId: bigint; contractId: bigint; kind: string; itemKey: string; name: string; qty: number; holder: string; recipient: Identity }
interface StationLike { stationId: bigint; worldId: bigint; settlementId: bigint; kind: string; x: number; z: number }

type Tab = 'board' | 'post' | 'mine';
interface SupplyRow { runId: bigint; battleId: bigint; side: string; originSid: bigint; outpostId: bigint; cargo: string; crates: number; reward: bigint; state: string; taker: Identity; deadline: { microsSinceUnixEpoch: bigint } }
interface OutpostRow { outpostId: bigint; battleId: bigint; side: string; x: number; z: number }
const CARGO_LABEL: Record<string, string> = { munitions: 'munitions', medical: 'medical supplies', rations: 'rations', parts: 'turret parts' };
/** A commodity a relief order asks for → the resource family keys that satisfy it. */
const FOOD_FAMILIES: Record<string, string[]> = { grain: ['grain'], produce: ['produce'], game: ['game'], fish: ['brine_catch'] };

export class AtlasContractBoardModule implements IModule {
  readonly name = 'AtlasContractBoard';
  private win: UiWindow | null = null;
  private station: { stationId: string; settlementId: string } | null = null;
  private tab: Tab = 'board';
  private postKind: 'courier' | 'escort' = 'courier';
  private listEl: HTMLDivElement | null = null;
  private subbed = false;
  private readonly rerender = debounced(() => this.renderList());

  init(_app: App): void {
    bus.on('atlas:open-contract-board', (p) => this.open(p));
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    bus.on('atlas:world-cleared', () => this.win?.setOpen(false));
    (window as unknown as Record<string, unknown>).__contractBoard = {
      open: (stationId = '0', settlementId = '0') => this.open({ stationId, settlementId }),
      close: () => this.win?.setOpen(false),
      tab: (t: Tab) => { this.tab = t; this.render(); },
      state: () => this.state(),
    };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try { conn.subscriptionBuilder().subscribe(['SELECT * FROM ground_contract', 'SELECT * FROM ground_parcel', 'SELECT * FROM ground_escort', 'SELECT * FROM war_supply_run', 'SELECT * FROM battle_outpost']); } catch { /* reconnect */ }
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const n of ['groundContract', 'groundParcel', 'playerResource', 'playerItem', 'warSupplyRun']) {
      const t = db[n];
      t?.onInsert?.(() => this.rerender()); t?.onUpdate?.(() => this.rerender()); t?.onDelete?.(() => this.rerender());
    }
  }

  // ── Rows ────────────────────────────────────────────────────────────────────
  private contracts(): ContractRow[] {
    const t = (atlas.conn?.db as unknown as { groundContract?: { iter(): Iterable<ContractRow> } } | undefined)?.groundContract;
    return t ? [...t.iter()] : [];
  }
  private parcels(contractId: bigint): ParcelRow[] {
    const t = (atlas.conn?.db as unknown as { groundParcel?: { iter(): Iterable<ParcelRow> } } | undefined)?.groundParcel;
    return t ? [...t.iter()].filter((p) => p.contractId === contractId) : [];
  }
  private nameOf(id: Identity): string {
    try { const a = atlas.conn?.db.account.identity.find(id); return a ? (a.displayName || a.username) : id.toHexString().slice(0, 8); } catch { return '?'; }
  }
  private boardOf(sid: bigint): StationLike | null {
    const t = (atlas.conn?.db as unknown as { activityStation?: { iter(): Iterable<StationLike> } } | undefined)?.activityStation;
    if (!t) return null;
    let best: StationLike | null = null, bd = Infinity;
    for (const s of t.iter()) {
      if (s.kind !== 'contract_board' || s.settlementId !== sid) continue;
      const d = Math.hypot(s.x - atlas.localX, s.z - atlas.localZ);
      if (d < bd) { bd = d; best = s; }
    }
    return best;
  }
  private here(): bigint { return BigInt(this.station?.settlementId ?? '0'); }

  // ── Window ──────────────────────────────────────────────────────────────────
  private open(p: { stationId: string; settlementId: string }): void {
    this.station = p;
    if (!this.win) {
      this.win = makeWindow({ id: 'contract-board', title: 'Contract Board', icon: 'ticket', width: 600, height: 600, minWidth: 460, minHeight: 360 });
      this.win.body.style.cssText += 'display:flex;flex-direction:column;gap:10px;padding:10px 12px;overflow:auto;font:13px var(--ax-font);color:var(--ax-text)';
    }
    const sid = Number(p.settlementId);
    this.win.setTitle(sid > 0 ? `Contract Board — ${townName(sid)}` : 'Contract Board');
    this.win.setOpen(true);
    this.render();
  }

  private render(): void {
    const w = this.win;
    if (!w) return;
    w.body.replaceChildren();
    w.body.appendChild(tabs([{ key: 'board', label: 'Board' }, { key: 'post', label: 'Post a contract' }, { key: 'mine', label: 'Mine' }], this.tab, (k) => { this.tab = k as Tab; this.render(); }));
    if (this.tab === 'post') { w.body.appendChild(this.postForm()); this.listEl = null; return; }
    this.listEl = el('div', 'display:flex;flex-direction:column;gap:8px');
    w.body.appendChild(this.listEl);
    this.renderList();
  }

  /** SOFT RENDER (2026-09-25): every contract / parcel / stack / item row anyone touched rebuilt the list — a relief
   *  quantity being typed was wiped with it. The fresh list is patched over the live one (lib/soft_render.ts). */
  private renderList(): void {
    const list = this.listEl;
    if (!list || !this.win?.isOpen()) return;
    softRender(list, (h) => this.buildList(h));
  }

  /** A parcel back at its ORIGIN board for me to collect: a courier contract that failed or lapsed (returnParcelToOrigin). */
  private returnedToMe(c: ContractRow, me: Identity | null): boolean {
    if (!me || c.kind !== 'courier' || (c.state !== 'failed' && c.state !== 'expired')) return false;
    return this.parcels(c.contractId).some((p) => p.holder === 'origin' && p.recipient.isEqual(me));
  }

  private buildList(list: HTMLElement): void {
    const me = atlas.conn?.identity ?? null;
    const here = this.here();
    const all = this.contracts();
    if (this.tab === 'board') {
      const players = all.filter((c) => c.state === 'open' && c.issuerKind === 'player' && c.originSid === here).sort((a, b) => Number(b.reward - a.reward));
      const relief = all.filter((c) => c.state === 'open' && c.kind === 'supply' && c.destSid === here);
      const escorts = all.filter((c) => c.state === 'open' && c.kind === 'escort' && c.issuerKind === 'town' && c.originSid === here);
      list.appendChild(head(`Posted here by travellers (${players.length})`));
      if (players.length === 0) list.appendChild(muted('No parcels or escorts leave from this board right now.'));
      for (const c of players) list.appendChild(this.contractCard(c, me));
      list.appendChild(head(`This town needs (${relief.length})`));
      if (relief.length === 0) list.appendChild(muted('The town\'s granaries hold for now — no relief orders.'));
      for (const c of relief) list.appendChild(this.contractCard(c, me));
      list.appendChild(head(`Caravans wanting an escort (${escorts.length})`));
      if (escorts.length === 0) list.appendChild(muted('No caravan has just left that wants company.'));
      for (const c of escorts) list.appendChild(this.contractCard(c, me));
      // THE WAR (2026-09-24): supply runs this town posts for its side's field outpost.
      const runs = this.supplyRuns().filter((r) => r.state === 'open' && r.originSid === here);
      if (runs.length) {
        list.appendChild(head(`The war needs supplies (${runs.length})`));
        list.appendChild(muted('Crates for this town\'s outpost at the front: load them on your hovercraft here and ride them in — the battle feels them at once (munitions steady the line, medicine staffs the aid station, rations feed everyone, parts raise a turret).'));
        for (const r of runs) list.appendChild(this.supplyCard(r, me));
      }
      // Anything open elsewhere, for the planner.
      const elsewhere = all.filter((c) => c.state === 'open' && c.issuerKind === 'player' && c.originSid !== here).length;
      if (elsewhere) list.appendChild(muted(`${elsewhere} more contracts are posted at other towns' boards.`));
      return;
    }
    const carrying = all.filter((c) => me && c.taker.isEqual(me) && (c.state === 'taken'));
    // A RETURNED parcel waits for me like a delivered one (2026-09-25) — listed under "Waiting for you", not lost in the
    // tail of "You posted".
    const returned = all.filter((c) => this.returnedToMe(c, me));
    const posted = all.filter((c) => me && c.issuerKind === 'player' && c.issuer.isEqual(me) && !returned.includes(c)).sort((a, b) => ms(b.postedAt) - ms(a.postedAt)).slice(0, 16);
    const forMe = [...all.filter((c) => me && c.state === 'held' && c.recipient.isEqual(me) && !c.issuer.isEqual(me)), ...returned];
    list.appendChild(head('You are carrying'));
    const myRuns = this.supplyRuns().filter((r) => me && r.state === 'taken' && r.taker.isEqual(me));
    if (carrying.length === 0 && myRuns.length === 0) list.appendChild(muted('Nothing — take a contract from a board.'));
    for (const c of carrying) list.appendChild(this.contractCard(c, me));
    for (const r of myRuns) list.appendChild(this.supplyCard(r, me));
    if (forMe.length) { list.appendChild(head('Waiting for you')); for (const c of forMe) list.appendChild(this.contractCard(c, me)); }
    list.appendChild(head('You posted'));
    if (posted.length === 0) list.appendChild(muted('You have posted nothing.'));
    for (const c of posted) list.appendChild(this.contractCard(c, me));
  }

  // ── War supply runs (2026-09-24) ────────────────────────────────────────────
  private supplyRuns(): SupplyRow[] {
    try { return [...((atlas.conn?.db as unknown as { warSupplyRun?: { iter(): Iterable<SupplyRow> } })?.warSupplyRun?.iter() ?? [])]; }
    catch { return []; }
  }
  private outpostOf(id: bigint): OutpostRow | null {
    try {
      for (const o of (atlas.conn?.db as unknown as { battleOutpost?: { iter(): Iterable<OutpostRow> } })?.battleOutpost?.iter() ?? []) if (o.outpostId === id) return o;
    } catch { /* not subscribed */ }
    return null;
  }
  private supplyCard(r: SupplyRow, me: Identity | null): HTMLDivElement {
    const c = card();
    c.setAttribute('data-row', `run:${r.runId}`); // rows pair by identity in the soft render
    const o = this.outpostOf(r.outpostId);
    c.appendChild(el('div', 'font-weight:600', `${r.crates} crates of ${CARGO_LABEL[r.cargo] ?? r.cargo} → the ${townName(r.originSid)} outpost at the front`));
    const row = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
    row.appendChild(chip(`${String(r.reward)} ⓒ`, 'good'));
    row.appendChild(chip(`due in ${untilText(ms(r.deadline) - Date.now())}`));
    if (o) {
      const d = Math.hypot(o.x - atlas.localX, o.z - atlas.localZ);
      row.appendChild(chip(`${d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} ${compassOf(o.x - atlas.localX, o.z - atlas.localZ)}`, 'info'));
    }
    c.appendChild(row);
    const conn = atlas.conn;
    if (!conn) return c;
    const acts = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    if (r.state === 'open') acts.appendChild(btn('Take — load it on your hovercraft', () => void act(conn.reducers.takeWarSupply({ runId: r.runId }), 'Loaded — ride it to the outpost', () => this.renderList()), true));
    else if (me && r.taker.isEqual(me)) {
      if (o) acts.appendChild(btn('Waypoint to the outpost', () => void act(conn.reducers.createWaypoint({ name: `Outpost: ${townName(r.originSid)}`.slice(0, 32), posX: o.x, posZ: o.z }), 'Waypoint set — ride in and unload (E)')));
      acts.appendChild(btn('Deliver (riding, at the outpost)', () => void act(conn.reducers.deliverWarSupply({ runId: r.runId }), 'Delivered — the front feels it', () => this.renderList())));
      acts.appendChild(btn('Abandon', () => void act(conn.reducers.abandonWarSupply({ runId: r.runId }), 'You left the crates for someone else', () => this.renderList())));
    }
    if (acts.childElementCount) c.appendChild(acts);
    return c;
  }

  private kindLabel(c: ContractRow): string {
    if (c.kind === 'courier') return 'Courier';
    if (c.kind === 'supply') return 'Relief';
    return c.issuerKind === 'town' ? 'Caravan escort' : 'Escort';
  }

  private contractCard(c: ContractRow, me: Identity | null): HTMLDivElement {
    const k = card();
    k.setAttribute('data-row', `contract:${c.contractId}`); // rows pair by identity in the soft render
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    const top = el('div', 'display:flex;align-items:center;gap:8px;flex-wrap:wrap');
    top.appendChild(chip(this.kindLabel(c), c.kind === 'supply' ? 'info' : ''));
    top.appendChild(el('span', 'font-weight:600;color:var(--ax-hi)', c.kind === 'supply' ? `${c.qty - c.delivered} ${c.commodity} for ${townName(c.destSid)}` : `${townName(c.originSid)} → ${townName(c.destSid)}`));
    top.appendChild(chip(`${c.reward} ⓒ${c.kind === 'supply' ? ' in all' : ''}`, 'warn'));
    if (c.collateral > 0n) top.appendChild(chip(`${c.collateral} ⓒ collateral`));
    const left = ms(c.deadline) - Date.now();
    if (c.state === 'open' || c.state === 'taken') top.appendChild(chip(left > 0 ? `${untilText(left)} left` : 'overdue', left > 0 ? '' : 'bad'));
    if (c.state !== 'open') top.appendChild(chip(c.state, c.state === 'done' ? 'good' : c.state === 'failed' ? 'bad' : ''));
    k.appendChild(top);
    const who = c.issuerKind === 'town' ? `posted by ${townName(c.kind === 'supply' ? c.destSid : c.originSid)}` : `posted by ${this.nameOf(c.issuer)}`;
    const cargo = c.kind === 'courier' ? this.parcels(c.contractId).map((p) => p.kind === 'stack' ? `${p.qty}× ${p.name}` : p.name).join(', ') : '';
    k.appendChild(muted(`${who}${cargo ? ` — the parcel: ${cargo}` : ''}${c.note ? ` — “${c.note}”` : ''}`));
    const row = el('div', 'display:flex;gap:6px;flex-wrap:wrap;align-items:center');
    const isIssuer = !!me && c.issuerKind === 'player' && c.issuer.isEqual(me);
    const isTaker = !!me && c.taker.isEqual(me);
    const here = this.here();
    if (c.state === 'open' && c.kind !== 'supply' && !isIssuer) {
      row.appendChild(btn(c.collateral > 0n ? `Take (post ${c.collateral} ⓒ)` : 'Take', () => void act(r?.takeGroundContract?.({ contractId: c.contractId }), c.kind === 'courier' ? `Parcel in hand — deliver it at ${townName(c.destSid)}'s board` : `You escort to ${townName(c.destSid)}`, () => { this.tab = 'mine'; this.render(); }), true));
    }
    if (c.state === 'open' && c.kind === 'supply') {
      const fams = FOOD_FAMILIES[c.commodity] ?? [];
      const stacks = this.myStacks().filter((s) => fams.includes(s.familyKey));
      if (stacks.length === 0) row.appendChild(muted(`Bring ${c.commodity} (${fams.map((f) => FAMILY_BY_KEY[f]?.label ?? f).join(' / ')}) to deliver.`));
      else {
        const pick = select(stacks.map((s) => ({ value: String(s.stackId), label: `${s.name} (${s.qty})` })));
        const qty = input('qty', String(Math.min(c.qty - c.delivered, stacks[0].qty)), 'number', '70px');
        pick.setAttribute('data-k', `relief-pick:${c.contractId}`);
        qty.setAttribute('data-k', `relief-qty:${c.contractId}`);
        row.appendChild(pick); row.appendChild(qty);
        // The fields ON SCREEN (2026-09-25, the soft render): a kept field holds the player's pick and quantity.
        row.appendChild(btn('Deliver', () => {
          const livePick = fieldByKey<HTMLSelectElement>(this.listEl, `relief-pick:${c.contractId}`) ?? pick;
          const liveQty = fieldByKey(this.listEl, `relief-qty:${c.contractId}`) ?? qty;
          void act(r?.deliverSupply?.({ contractId: c.contractId, stackId: BigInt(livePick.value), qty: Math.max(1, Math.floor(Number(liveQty.value) || 1)) }), `Delivered to ${townName(c.destSid)} — thank you`);
        }, true));
      }
    }
    if (c.state === 'taken' && isTaker) {
      if (c.kind === 'courier') {
        row.appendChild(btn('Deliver here', () => void act(r?.deliverCourier?.({ contractId: c.contractId }), 'Delivered — you are paid and your collateral returned'), true, `At ${townName(c.destSid)}'s board`));
      }
      if (c.kind === 'escort' && c.issuerKind === 'player') row.appendChild(muted(`Stay within ${60} m of ${this.nameOf(c.issuer)} on the road; they sign it off at ${townName(c.destSid)}.`));
      if (c.kind === 'escort' && c.issuerKind === 'town') row.appendChild(muted('Ride with the caravan until it arrives; you are paid on arrival if you stayed near it.'));
      row.appendChild(btn('Abandon', () => void act(r?.abandonGroundContract?.({ contractId: c.contractId }), 'Abandoned — your collateral is forfeit')));
    }
    if (isIssuer && c.state === 'open') row.appendChild(btn('Cancel', () => void act(r?.cancelGroundContract?.({ contractId: c.contractId }), 'Cancelled — the reward is refunded and the parcel returned')));
    if (isIssuer && c.state === 'taken' && c.kind === 'escort') row.appendChild(btn('Sign off here', () => void act(r?.completeEscort?.({ contractId: c.contractId }), 'Escort signed off — your escort is paid'), true, `At ${townName(c.destSid)}'s board, with your escort beside you`));
    if (c.state === 'held' && me && c.recipient.isEqual(me)) row.appendChild(btn('Collect the parcel', () => void act(r?.collectParcel?.({ contractId: c.contractId }), 'Collected — the parcel is in your pack'), true, `At ${townName(c.destSid)}'s board`));
    // A RETURNED PARCEL (2026-09-25): a courier contract that failed or lapsed sends its goods back to the ORIGIN board,
    // held for the issuer (returnParcelToOrigin) — collectParcel takes them there, but the card had no button: the
    // goods sat on the board until the history prune.
    const returned = this.returnedToMe(c, me);
    if (returned) row.appendChild(btn('Collect the returned parcel', () => void act(r?.collectParcel?.({ contractId: c.contractId }), 'Collected — your parcel is back in your pack'), true, `At ${townName(c.originSid)}'s board, where you posted it`));
    // The way there.
    if (returned && c.originSid !== here) {
      const b = this.boardOf(c.originSid);
      if (b) {
        const d = Math.hypot(b.x - atlas.localX, b.z - atlas.localZ);
        row.appendChild(chip(`${(d / 1000).toFixed(1)} km ${compassOf(b.x - atlas.localX, b.z - atlas.localZ)}`));
        row.appendChild(btn('Waypoint', () => void act(atlas.conn?.reducers.createWaypoint({ name: `Board: ${townName(c.originSid)}`.slice(0, 32), posX: b.x, posZ: b.z }), 'Waypoint set on the board that holds your parcel')));
      }
    }
    if ((c.state === 'taken' || c.state === 'held') && c.destSid !== here) {
      const b = this.boardOf(c.destSid);
      if (b) {
        const d = Math.hypot(b.x - atlas.localX, b.z - atlas.localZ);
        row.appendChild(chip(`${(d / 1000).toFixed(1)} km ${compassOf(b.x - atlas.localX, b.z - atlas.localZ)}`));
        row.appendChild(btn('Waypoint', () => void act(atlas.conn?.reducers.createWaypoint({ name: `Board: ${townName(c.destSid)}`.slice(0, 32), posX: b.x, posZ: b.z }), 'Waypoint set on the destination board')));
      }
    }
    if (row.childElementCount) k.appendChild(row);
    return k;
  }

  private myStacks(): Array<{ stackId: bigint; familyKey: string; name: string; qty: number }> {
    const me = atlas.conn?.identity;
    const t = (atlas.conn?.db as unknown as { playerResource?: { iter(): Iterable<{ stackId: bigint; owner: Identity; familyKey: string; name: string; qty: number }> } } | undefined)?.playerResource;
    if (!t || !me) return [];
    return [...t.iter()].filter((s) => s.owner.isEqual(me) && s.qty > 0);
  }
  private myItems(): Array<{ itemId: bigint; name: string; itemKey: string; quality: number }> {
    const me = atlas.conn?.identity;
    const conn = atlas.conn;
    if (!conn || !me) return [];
    // An equipped piece lives in player_equipment, not the bag — every bag row is packable.
    return [...conn.db.playerItem.iter()].filter((i) => i.owner.isEqual(me));
  }

  private postForm(): HTMLDivElement {
    const f = el('div', 'display:flex;flex-direction:column;gap:9px');
    f.appendChild(tabs([{ key: 'courier', label: 'Courier a parcel' }, { key: 'escort', label: 'Hire an escort' }], this.postKind, (k) => { this.postKind = k as 'courier' | 'escort'; this.render(); }));
    const here = Number(this.station?.settlementId ?? 0);
    const towns = townList().filter((t) => t.sid !== here)
      .sort((a, b) => Math.hypot(a.x - atlas.localX, a.z - atlas.localZ) - Math.hypot(b.x - atlas.localX, b.z - atlas.localZ))
      .slice(0, 80);
    const dest = select(towns.map((t) => ({ value: String(t.sid), label: `${t.name} — ${(Math.hypot(t.x - atlas.localX, t.z - atlas.localZ) / 1000).toFixed(1)} km` })));
    const reward = input(`reward (≥ ${CONTRACT_MIN_REWARD})`, '60', 'number', '120px');
    const hours = select([1, 2, 4, 8, 12, 24, 48, 72].map((h) => ({ value: String(h), label: `${h} h to deliver` })), String(CONTRACT_HOURS_DEFAULT));
    const note = input('a note (optional)', '', 'text', '100%');
    note.maxLength = 120;
    const cost = el('div', 'color:var(--ax-soft)');
    const upd = (): void => { const v = Math.max(0, Math.floor(Number(reward.value) || 0)); cost.textContent = `You pay ${v + contractFee(v)} ⓒ now (${v} reward + ${contractFee(v)} board fee).`; };
    reward.addEventListener('input', upd); upd();
    const r = atlas.conn?.reducers as unknown as Record<string, (a: unknown) => Promise<void>> | undefined;
    if (this.postKind === 'escort') {
      f.appendChild(muted('Someone walks beside you to a town. The road is sampled: your escort must stay near you most of the way. You sign it off at the destination board with your escort beside you.'));
      f.appendChild(field('To', dest)); f.appendChild(field('Reward', reward, cost)); f.appendChild(field('Time', hours)); f.appendChild(field('Note', note));
      f.appendChild(btn('Post the escort', () => void act(r?.postEscortContract?.({ destSid: BigInt(dest.value), reward: BigInt(Math.floor(Number(reward.value) || 0)), hours: Number(hours.value), note: note.value.trim() }), 'Escort posted on this board', () => { this.tab = 'mine'; this.render(); }), true));
      return f;
    }
    f.appendChild(muted(`Pack up to ${PARCEL_MAX_ITEMS} items and/or part of a stack; a courier carries it to the destination board. The parcel is held there for the recipient (you, unless you name someone). The courier posts the collateral you ask, returned on delivery.`));
    const items = this.myItems().slice(0, 60);
    const box = el('div', 'display:flex;flex-direction:column;gap:3px;max-height:150px;overflow:auto;padding:4px 6px;border:1px solid var(--ax-line);border-radius:var(--ax-radius)');
    const picks = new Set<string>();
    if (items.length === 0) box.appendChild(muted('No unequipped items in your pack.'));
    for (const it of items) {
      const lab = el('label', 'display:flex;gap:6px;align-items:center;cursor:pointer');
      const cb = document.createElement('input');
      cb.type = 'checkbox';
      cb.addEventListener('change', () => { if (cb.checked) { if (picks.size >= PARCEL_MAX_ITEMS) { cb.checked = false; return; } picks.add(String(it.itemId)); } else picks.delete(String(it.itemId)); });
      lab.appendChild(cb);
      lab.appendChild(el('span', '', `${it.name || it.itemKey} (q${it.quality})`));
      box.appendChild(lab);
    }
    const stacks = this.myStacks();
    const stack = select([{ value: '0', label: '— no stack —' }, ...stacks.map((s) => ({ value: String(s.stackId), label: `${s.name} (${s.qty})` }))]);
    const sqty = input('qty', '0', 'number', '70px');
    const collateral = input('collateral', '0', 'number', '110px');
    const recipient = input('recipient username (optional)', '', 'text', '220px');
    f.appendChild(field('Items', box));
    f.appendChild(field('Stack', stack, sqty));
    f.appendChild(field('To', dest));
    f.appendChild(field('Reward', reward, cost));
    f.appendChild(field('Collateral', collateral));
    f.appendChild(field('Recipient', recipient));
    f.appendChild(field('Time', hours));
    f.appendChild(field('Note', note));
    f.appendChild(btn('Post the parcel', () => void act(r?.postCourierContract?.({
      destSid: BigInt(dest.value), itemIds: [...picks].map((s) => BigInt(s)), stackId: BigInt(stack.value), stackQty: Math.max(0, Math.floor(Number(sqty.value) || 0)),
      reward: BigInt(Math.floor(Number(reward.value) || 0)), collateral: BigInt(Math.max(0, Math.floor(Number(collateral.value) || 0))),
      recipientUsername: recipient.value.trim(), hours: Number(hours.value), note: note.value.trim(),
    }), 'Parcel posted — it waits here for a courier', () => { this.tab = 'mine'; this.render(); }), true));
    return f;
  }

  private state(): Record<string, unknown> {
    const here = this.here();
    const all = this.contracts();
    return {
      open: !!this.win?.isOpen(), tab: this.tab, station: this.station,
      here: all.filter((c) => c.state === 'open' && ((c.issuerKind === 'player' && c.originSid === here) || (c.kind === 'supply' && c.destSid === here))).length,
      mine: all.filter((c) => atlas.conn?.identity && (c.taker.isEqual(atlas.conn.identity) || c.issuer.isEqual(atlas.conn.identity))).length,
    };
  }
}
